import { Button, cn, Input, useDebounceCallback } from "@devhop/ui";
import { ChevronDown, Loader2, PackageIcon } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import type { ShopSellableCatalogRow } from "@/modules/merchant/presentation/api/client";
import {
  usePosCategoriesQuery,
  usePosSellableBrowseQuery,
} from "@/modules/merchant/presentation/api/queries";
import {
  getStoredMerchantPosCategoryId,
  setStoredMerchantPosCategoryId,
} from "@/modules/merchant/presentation/lib/merchant-pos-category-storage";
import { formatPrice } from "@/shared/lib/currency";
import { resolveImageSrc } from "@/shared/ui/AppImage";
import { CategoryTreePopover } from "./CategoryTreePopover";

const PAGE_SIZE = 24;

type ShopPosProductBrowseProps = {
  shopId: string;
  branchId: string;
  disabled?: boolean;
  /** ຄອລຳນ໌ແຄບໃນໂໝດງ່າຍ desktop */
  compact?: boolean;
  /** mobile: ພັບເປิดເພື່ອປະຫຍັດພື້ນທີ່ */
  collapsibleOnMobile?: boolean;
  onSelectProduct: (row: ShopSellableCatalogRow) => void;
};

export function ShopPosProductBrowse({
  shopId,
  branchId,
  disabled = false,
  compact = false,
  collapsibleOnMobile = false,
  onSelectProduct,
}: ShopPosProductBrowseProps) {
  const [mobileExpanded, setMobileExpanded] = useState(false);
  const [categoryId, setCategoryId] = useState<string | null>(() =>
    getStoredMerchantPosCategoryId(shopId, branchId),
  );
  const [searchValue, setSearchValue] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [limit, setLimit] = useState(PAGE_SIZE);

  const debouncedSyncQ = useDebounceCallback((val: string) => {
    setDebouncedQ(val.trim());
    setLimit(PAGE_SIZE);
  }, 350);

  useEffect(() => {
    setCategoryId(getStoredMerchantPosCategoryId(shopId, branchId));
    setSearchValue("");
    setDebouncedQ("");
    setLimit(PAGE_SIZE);
  }, [shopId, branchId]);

  const categoriesQuery = usePosCategoriesQuery(shopId, branchId);
  const sellableQuery = usePosSellableBrowseQuery(shopId, {
    limit,
    offset: 0,
    q: debouncedQ || undefined,
    categoryId: categoryId ?? undefined,
    branchId,
  });

  const categoryItems = useMemo(
    () =>
      (categoriesQuery.data?.items ?? []).map((c) => ({
        id: c.id,
        name: c.name,
        parentId: c.parentId ?? null,
      })),
    [categoriesQuery.data?.items],
  );

  const categoryById = useMemo(
    () => new Map((categoriesQuery.data?.items ?? []).map((c) => [c.id, c])),
    [categoriesQuery.data?.items],
  );

  const topCategoryIds = categoriesQuery.data?.topCategoryIds ?? [];
  const topCategories = useMemo(
    () =>
      topCategoryIds
        .map((id) => categoryById.get(id))
        .filter((c): c is NonNullable<typeof c> => !!c),
    [topCategoryIds, categoryById],
  );

  const items = sellableQuery.data?.data ?? [];
  const total = sellableQuery.data?.meta.total ?? 0;
  const hasMore = items.length < total;

  useEffect(() => {
    if (!categoriesQuery.data?.items || !categoryId) return;
    const valid = categoriesQuery.data.items.some((c) => c.id === categoryId);
    if (!valid) {
      setCategoryId(null);
      setStoredMerchantPosCategoryId(shopId, branchId, null);
    }
  }, [categoriesQuery.data?.items, categoryId, shopId, branchId]);

  const applyCategory = (next: string | null) => {
    setCategoryId(next);
    setLimit(PAGE_SIZE);
    setStoredMerchantPosCategoryId(shopId, branchId, next);
  };

  const isCollapsible = compact && collapsibleOnMobile;
  const bodyHidden = isCollapsible && !mobileExpanded;

  return (
    <div
      className={cn(
        "overflow-hidden rounded-xl border border-border bg-card shadow-sm",
        compact ? "mb-0" : "mb-4",
      )}
    >
      {isCollapsible ? (
        <button
          type="button"
          className="flex w-full items-center justify-between border-border border-b px-3 py-3 text-start sm:px-4 lg:pointer-events-none lg:cursor-default"
          onClick={() => setMobileExpanded((v) => !v)}
          aria-expanded={mobileExpanded}
        >
          <div>
            <p className="font-semibold text-card-foreground text-sm">
              ເລືອກສິນຄ້າ
            </p>
            <p className="mt-0.5 text-[11px] text-muted-foreground lg:hidden">
              {mobileExpanded ? "ແຕະເພື່ອພັບ" : "ແຕະເພື່ອເປີດລາຍການ"}
            </p>
          </div>
          <ChevronDown
            className={cn(
              "size-5 shrink-0 text-muted-foreground transition-transform lg:hidden",
              mobileExpanded && "rotate-180",
            )}
            aria-hidden
          />
        </button>
      ) : (
      <div className="border-border border-b px-3 py-2.5 sm:px-4">
        <p className="font-semibold text-card-foreground text-sm">
          {compact ? "ເລືອກສິນຄ້າ" : "ເລືອກສິນຄ້າຈາກໝວດ"}
        </p>
        {!compact ? (
          <p className="mt-0.5 text-[11px] text-muted-foreground">
            ແຕະຮູບ ຫຼື ຊື່ເພື່ອເພີ່ມໃສ່ກະຕ່າ — ບໍ່ຕ້ອງຈື barcode
          </p>
        ) : null}
      </div>
      )}

      <div className={cn(bodyHidden && "hidden lg:block")}>
        <div
          className={cn(
            "flex flex-col gap-2 border-border border-b px-3 py-2 sm:px-4",
            compact ? "sm:flex-col" : "sm:flex-row sm:items-center",
          )}
        >
        <Input
          placeholder="ຄົ້ນຫາ: ຊື່, barcode, OEM..."
          value={searchValue}
          disabled={disabled}
          onChange={(e) => {
            const v = e.target.value;
            setSearchValue(v);
            debouncedSyncQ(v);
          }}
          className={cn(
            "h-10 flex-1 text-base",
            compact ? "w-full" : "sm:max-w-md",
          )}
        />
        <div
          className={cn(
            "w-full",
            compact ? "" : "sm:w-64",
            disabled && "pointer-events-none opacity-50",
          )}
        >
          <CategoryTreePopover
            categories={categoryItems}
            value={categoryId ?? ""}
            onChange={(val) => applyCategory(val || null)}
            isLoading={categoriesQuery.isLoading}
            clearable
            className="h-10"
          />
        </div>
        {(categoryId || debouncedQ) && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-10 shrink-0"
            disabled={disabled}
            onClick={() => {
              setSearchValue("");
              debouncedSyncQ("");
              applyCategory(null);
            }}
          >
            ລ້າງ
          </Button>
        )}
      </div>

      <div className="-mx-1 flex gap-2 overflow-x-auto px-3 py-2.5 [scrollbar-width:thin] sm:px-4">
        <button
          type="button"
          disabled={disabled}
          onClick={() => applyCategory(null)}
          className={cn(
            "shrink-0 rounded-full border px-4 py-2 font-medium text-sm transition-colors",
            "min-h-10 touch-manipulation",
            categoryId == null
              ? "border-primary bg-primary text-primary-foreground"
              : "border-border bg-background text-foreground hover:bg-muted",
          )}
        >
          ທັງໝົດ
        </button>
        {categoriesQuery.isLoading ? (
          <span className="flex items-center gap-2 px-2 text-muted-foreground text-xs">
            <Loader2 className="size-3.5 animate-spin" />
            ໂຫຼດໝວດ…
          </span>
        ) : (
          topCategories.map((cat) => (
            <button
              key={cat.id}
              type="button"
              disabled={disabled}
              onClick={() => applyCategory(cat.id)}
              className={cn(
                "shrink-0 rounded-full border px-4 py-2 font-medium text-sm transition-colors",
                "min-h-10 touch-manipulation",
                categoryId === cat.id
                  ? "border-primary bg-primary text-primary-foreground"
                  : "border-border bg-background text-foreground hover:bg-muted",
              )}
            >
              {cat.name}
              {cat.count > 0 ? (
                <span className="ml-1.5 opacity-70">({cat.count})</span>
              ) : null}
              <span className="ml-1 rounded bg-secondary/80 px-1 py-0.5 text-[9px] uppercase">
                ຍອດນິຍົມ
              </span>
            </button>
          ))
        )}
      </div>

      <div
        className={cn(
          "overflow-y-auto p-3 sm:p-4",
          compact
            ? "max-h-[min(50vh,26rem)]"
            : "max-h-[min(42vh,22rem)]",
        )}
      >
        {sellableQuery.isLoading && items.length === 0 ? (
          <div className="flex items-center justify-center gap-2 py-12 text-muted-foreground text-sm">
            <Loader2 className="size-5 animate-spin" />
            ກຳລັງໂຫຼດສິນຄ້າ…
          </div>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-2 py-12 text-center text-muted-foreground text-sm">
            <PackageIcon className="size-8 opacity-40" aria-hidden />
            ບໍ່ພົບສິນຄ້າ — ລອງຄົ້ນຫຼືເລືອກໝວດອື່ນ
          </div>
        ) : (
          <>
            <div
              className={cn(
                "grid grid-cols-2 gap-2",
                !compact && "sm:grid-cols-3 lg:grid-cols-4",
              )}
            >
              {items.map((row) => {
                const src = row.imageUrl ? resolveImageSrc(row.imageUrl) : null;
                const outOfStock =
                  row.quantityOnHand !== null && row.quantityOnHand <= 0;
                return (
                  <button
                    key={row.id}
                    type="button"
                    disabled={disabled || outOfStock}
                    title={
                      outOfStock
                        ? "ສະຕ໋ອກບໍ່ພໍທີ່ສາຂານີ້"
                        : (row.categoryName ?? undefined)
                    }
                    onClick={() => onSelectProduct(row)}
                    className={cn(
                      "flex flex-col overflow-hidden rounded-xl border border-border bg-background text-start",
                      "min-h-[7.5rem] touch-manipulation transition-colors",
                      "hover:border-primary/40 hover:bg-muted/30",
                      "disabled:cursor-not-allowed disabled:opacity-50",
                    )}
                  >
                    <div className="relative aspect-[4/3] w-full bg-muted">
                      {src ? (
                        <img
                          src={src}
                          alt=""
                          className="size-full object-cover"
                        />
                      ) : (
                        <div className="flex size-full items-center justify-center">
                          <PackageIcon className="size-8 text-muted-foreground/50" />
                        </div>
                      )}
                      {row.categoryName ? (
                        <span className="absolute bottom-1 left-1 max-w-[90%] truncate rounded bg-background/90 px-1.5 py-0.5 text-[9px] text-muted-foreground">
                          {row.categoryName}
                        </span>
                      ) : null}
                    </div>
                    <div className="flex flex-1 flex-col gap-0.5 p-2">
                      <p className="line-clamp-2 font-medium text-card-foreground text-xs leading-snug">
                        {row.productName}
                      </p>
                      <p className="font-semibold text-primary text-sm tabular-nums">
                        {formatPrice(row.price, row.currency)}
                      </p>
                      {row.quantityOnHand !== null ? (
                        <p
                          className={cn(
                            "text-[10px]",
                            outOfStock
                              ? "text-destructive"
                              : "text-muted-foreground",
                          )}
                        >
                          ຄົງ {row.quantityOnHand}
                        </p>
                      ) : null}
                    </div>
                  </button>
                );
              })}
            </div>
            {hasMore ? (
              <div className="mt-3 flex justify-center">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-10 min-w-[8rem]"
                  disabled={disabled || sellableQuery.isFetching}
                  onClick={() => setLimit((l) => l + PAGE_SIZE)}
                >
                  {sellableQuery.isFetching ? (
                    <>
                      <Loader2 className="mr-2 size-4 animate-spin" />
                      ໂຫຼດ…
                    </>
                  ) : (
                    `ເພີ່ມ (${items.length}/${total})`
                  )}
                </Button>
              </div>
            ) : null}
          </>
        )}
      </div>
      </div>
    </div>
  );
}
